import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { sendFeedback } from "@/lib/rota.functions";

/**
 * Formulário de reporte de divergência (praça de pedágio, tarifa ou rota).
 * Usa o mesmo canal de feedback já existente — sem backend novo.
 */
export function TollReportForm() {
  const submit = useServerFn(sendFeedback);
  const [route, setRoute] = useState("");
  const [plaza, setPlaza] = useState("");
  const [expected, setExpected] = useState("");
  const [details, setDetails] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (details.trim().length < 5 && plaza.trim().length < 3) {
      toast.error("Descreva a divergência para podermos conferir.");
      return;
    }
    const message = [
      "[Reporte de divergência]",
      route.trim() ? `Rota: ${route.trim()}` : null,
      plaza.trim() ? `Praça/rodovia: ${plaza.trim()}` : null,
      expected.trim() ? `Valor esperado: ${expected.trim()}` : null,
      details.trim() ? `Detalhes: ${details.trim()}` : null,
    ]
      .filter(Boolean)
      .join("\n");

    setSending(true);
    try {
      await submit({ data: { message, contactEmail: email.trim() } });
      setRoute("");
      setPlaza("");
      setExpected("");
      setDetails("");
      setEmail("");
      toast.success("Reporte enviado. Obrigado por ajudar a melhorar a base!");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Não foi possível enviar seu reporte.");
    } finally {
      setSending(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3 rounded-2xl border border-border bg-card p-5">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="report-route">Rota (origem e destino)</Label>
          <Input
            id="report-route"
            maxLength={200}
            value={route}
            onChange={(e) => setRoute(e.target.value)}
            placeholder="Ex.: Campinas (SP) → São José do Rio Preto (SP)"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="report-plaza">Praça e rodovia</Label>
          <Input
            id="report-plaza"
            maxLength={200}
            value={plaza}
            onChange={(e) => setPlaza(e.target.value)}
            placeholder="Ex.: Praça de Araraquara, SP-310"
          />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="report-expected">Valor cobrado na praça (opcional)</Label>
        <Input
          id="report-expected"
          maxLength={60}
          value={expected}
          onChange={(e) => setExpected(e.target.value)}
          placeholder="Ex.: R$ 14,20 para carro, sentido interior"
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="report-details">O que está divergente</Label>
        <Textarea
          id="report-details"
          rows={4}
          maxLength={1500}
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          placeholder="Ex.: a praça não apareceu na estimativa; ou o valor exibido está abaixo do cobrado."
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="report-email">E-mail para contato (opcional)</Label>
        <Input
          id="report-email"
          type="email"
          maxLength={255}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="voce@email.com"
        />
      </div>
      <Button type="submit" disabled={sending} className="w-full sm:w-auto">
        {sending ? <Loader2 className="size-4 animate-spin" /> : null}
        Enviar reporte
      </Button>
    </form>
  );
}
